/**
 * resumeSignature — the *return* half of CB-5.5 signature adoption for CB-5.9 resume (FR-54 / UX-DR21).
 *
 * The adopted signature is held only in SignContractPageV2 session state (`adoptedSignature`), so on a
 * fresh page load it is null and the step machine would route a returning signer back to `adopt` even
 * though they already applied their signature to fields last session. But CB-5.6 persisted that
 * signature server-side: applying it writes the `signatureFieldValue` JSONB to each signature/initials
 * field's `contract_fields.value` via the token-scoped fill RPC. This module reverses that shape so the
 * shell can rehydrate `adoptedSignature` from the signer's OWN already-filled fields and land them in
 * `review`, not `adopt`.
 *
 * Pure + headlessly testable (no DB, no component) — the resume read itself stays token-scoped in the
 * shell, which only ever passes this the returning signer's own fields.
 */

import type { SigningFieldV2 } from '@/types/contracts';
import {
  type SignatureValue,
  type SignatureMethod,
  hasSignature,
} from '@/components/settings/contracts/storedSignature';

const SIGNATURE_FIELD_TYPES = new Set<SigningFieldV2['field_type']>(['signature', 'initials']);
const SIGNATURE_METHODS = new Set<SignatureMethod>(['typed', 'drawn', 'uploaded']);

/** Recover the FR-56 method from the persisted value, so re-applying after resume keeps it (CB-5.10). */
function methodFrom(value: Record<string, unknown>): SignatureMethod | null {
  const m = value.method;
  return typeof m === 'string' && SIGNATURE_METHODS.has(m as SignatureMethod) ? (m as SignatureMethod) : null;
}

/**
 * Reconstruct a SignatureValue from the persisted JSONB of a filled signature/initials field — the
 * inverse of `signatureFieldValue` (fieldFill.ts). Prefers the explicit `image`/`typed`/`style` keys
 * that adoption writes; falls back to the discriminated `value`+`kind` shape defensively. Returns null
 * when the value carries no real signature (so resume doesn't treat an empty field as adopted).
 */
export function adoptedSignatureFromValue(
  value: Record<string, unknown> | null | undefined,
): SignatureValue | null {
  if (!value) return null;

  const image = typeof value.image === 'string' && value.image ? value.image : null;
  const typed = typeof value.typed === 'string' && value.typed ? value.typed : null;
  const style = typeof value.style === 'string' && value.style ? value.style : null;

  const method = methodFrom(value);

  if (image || typed) {
    const sig: SignatureValue = {
      signature_image: image,
      signature_typed: typed,
      signature_style: style,
      signature_method: method,
    };
    return hasSignature(sig) ? sig : null;
  }

  // Defensive fallback: a value that stored only the discriminated `value` (+ optional `kind`/`style`).
  const inner = typeof value.value === 'string' && value.value ? value.value : null;
  if (!inner) return null;
  const sig: SignatureValue =
    value.kind === 'image'
      ? { signature_image: inner, signature_typed: null, signature_style: null, signature_method: method }
      : { signature_image: null, signature_typed: inner, signature_style: style, signature_method: method };
  return hasSignature(sig) ? sig : null;
}

/**
 * Pick the adopted signature to rehydrate from the signer's own fields: the first FILLED
 * signature/initials field. Returns null when none is filled (the signer hasn't adopted yet, so resume
 * correctly lands them on `adopt`). Only ever fed the returning signer's own token-scoped fields.
 */
export function rehydrateAdoptedSignature(fields: SigningFieldV2[]): SignatureValue | null {
  for (const f of fields) {
    if (!SIGNATURE_FIELD_TYPES.has(f.field_type)) continue;
    if (!f.filled_at) continue;
    const sig = adoptedSignatureFromValue(f.value);
    if (sig) return sig;
  }
  return null;
}
